import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Phone, Mail, MapPin, Eye, X } from 'lucide-react';
import CustomerCreationPanel from './CustomerCreationPanel';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { formatDate } from '../utils/dateFormat';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const CustomerManagement = () => {
  const { hasPermission } = usePermissions();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data.customers || []);
    } catch (error) {
      toast.error('Failed to load customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerCreated = (newCustomer) => {
    setCustomers(prev => [newCustomer, ...prev]);
    toast.success('Customer added successfully!');
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setShowCreatePanel(true);
  };

  const handleCustomerUpdated = (updatedCustomer) => {
    setCustomers(prev => prev.map(customer => 
      customer.id === updatedCustomer.id ? updatedCustomer : customer
    ));
    toast.success('Customer updated successfully!');
    setEditingCustomer(null);
  };

  const handleClosePanel = () => {
    setShowCreatePanel(false);
    setEditingCustomer(null);
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      await axios.delete(`/api/customers/${customerId}`);
      setCustomers(prev => prev.filter(customer => customer.id !== customerId));
      toast.success('Customer deleted successfully');
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const filteredCustomers = (customers || []).filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.contact && customer.contact.includes(searchTerm)) ||
    (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getTypeColor = (type) => {
    return type === 'B2B' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
  };

  const getFeedbackColor = (feedback) => {
    switch (feedback) {
      case 'EXCELLENT':
        return 'bg-green-100 text-green-800';
      case 'GOOD':
        return 'bg-blue-100 text-blue-800';
      case 'AVERAGE':
        return 'bg-yellow-100 text-yellow-800';
      case 'POOR':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
          <p className="text-gray-600 mt-1">
            Manage your customer database and create new customer profiles
          </p>
        </div>
        {hasPermission('customers.create') && (
          <button
            onClick={() => setShowCreatePanel(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Customer
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search customers by name, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="text-sm text-gray-500">
            {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Customers List */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-500">
              {searchTerm ? 'No customers found matching your search.' : 'No customers yet.'}
            </div>
            {!searchTerm && hasPermission('customers.create') && (
              <button
                onClick={() => setShowCreatePanel(true)}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Create your first customer
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredCustomers.map((customer) => (
              <div key={customer.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {customer.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(customer.type || 'B2C')}`}>
                        {customer.type || 'B2C'}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getFeedbackColor(customer.feedback || 'UNRATED')}`}>
                        {customer.feedback || 'UNRATED'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      {customer.contact && (
                        <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4" />
                          <span>{customer.contact}</span>
                        </div>
                      )}
                      {customer.email && (
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4" />
                          <span>{customer.email}</span>
                        </div>
                      )}
                      {customer.city && (
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4" />
                          <span>{customer.city}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-2 text-xs text-gray-500">
                      {customer.leadSource && `Lead Source: ${customer.leadSource} • `}
                      Created: {formatDate(customer.createdAt)}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button 
                      onClick={() => setViewingCustomer(customer)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                      title="View"
                    >
                      👁️
                    </button>
                    {hasPermission('customers.update') && (
                      <button 
                        onClick={() => handleEditCustomer(customer)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                        title="Edit"
                      >
                        ✏️
                      </button>
                    )}
                    {hasPermission('customers.delete') && (
                      <button 
                        onClick={() => handleDeleteCustomer(customer.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Customer Creation Panel */}
      <CustomerCreationPanel
        isOpen={showCreatePanel}
        onClose={handleClosePanel}
        onCustomerCreated={editingCustomer ? handleCustomerUpdated : handleCustomerCreated}
        editingCustomer={editingCustomer}
      />

      {/* Customer Details Popup */}
      {viewingCustomer && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setViewingCustomer(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-semibold">Customer Details</h3>
                <button onClick={() => setViewingCustomer(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div><strong>Name:</strong> {viewingCustomer.name}</div>
                <div><strong>Contact:</strong> {viewingCustomer.contact || 'Not provided'}</div>
                <div><strong>Email:</strong> {viewingCustomer.email || 'Not provided'}</div>
                <div><strong>Type:</strong> {viewingCustomer.type || 'B2C'}</div>
                <div><strong>City:</strong> {viewingCustomer.city || 'Not provided'}</div>
                <div><strong>Feedback:</strong> {viewingCustomer.feedback || 'UNRATED'}</div>
                <div><strong>Lead Source:</strong> {viewingCustomer.leadSource || 'Not provided'}</div>
                <div><strong>Created:</strong> {formatDate(viewingCustomer.createdAt)}</div>
                <div><strong>ID:</strong> <span className="font-mono text-xs">{viewingCustomer.id}</span></div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerManagement;
